import type { DealAnalysis } from '../types';
import { STRATEGY_THRESHOLDS, type StrategyThresholds, type InvestmentStrategy } from './financialCalculations';

export type Archetype =
  | 'stnl_nnn'
  | 'multi_tenant_retail'
  | 'value_add_leaseup'
  | 'owner_user'
  | 'industrial_multi'
  | 'ground_lease'
  | 'office'
  | 'multifamily'
  | 'land_development';

export type ProfileType =
  | 'credit_tenant_yield'
  | 'core'
  | 'core_plus'
  | 'value_add'
  | 'opportunistic'
  | 'owner_user'
  | 'custom';

export type CapitalStructure =
  | 'equity_first'
  | 'ltv_driven'
  | 'all_cash'
  | 'assume_existing'
  | 'seller_carry'
  | 'sba_504'
  | 'sba_7a';

export interface ArchetypePreset {
  key: Archetype;
  label: string;
  defaults: Partial<DealAnalysis>;
  defaultProfile: ProfileType;
  suppressedMetrics: string[];
  descriptorTemplate: string;
}

/**
 * Thresholds by investor profile. The four strategy rows are re-exported from
 * STRATEGY_THRESHOLDS verbatim: saved analyses reference them by
 * investment_strategy, so changing a value would silently re-score delivered work.
 * credit_tenant_yield comes from "Bear Valley - Agent Reference Guide.md" section 6.
 */
export const PROFILE_THRESHOLDS: Record<ProfileType, StrategyThresholds | null> = {
  credit_tenant_yield: { cap_rate: 5.75, cash_on_cash: 4.0, irr: 7.0, equity_multiple: 1.25, dscr: 1.1 },
  core: STRATEGY_THRESHOLDS.core,
  core_plus: STRATEGY_THRESHOLDS.core_plus,
  value_add: STRATEGY_THRESHOLDS.value_add,
  opportunistic: STRATEGY_THRESHOLDS.opportunistic,
  owner_user: null, // scored on occupancy cost vs market rent, not return metrics
  custom: null,
};

/** The threshold columns InvestmentAnalysis.tsx reads off an investor_profiles row. */
export interface InvestorProfileThresholds {
  threshold_cap_rate?: number | null;
  threshold_cash_on_cash?: number | null;
  threshold_irr?: number | null;
  threshold_equity_multiple?: number | null;
  threshold_dscr?: number | null;
}

/**
 * Single source of truth for "which bar does this deal have to clear".
 *
 * The selected investor profile WINS whenever one is set; otherwise fall back
 * to the saved investment_strategy. Joseph's ruling 2026-07-31.
 *
 * Why this exists: Phase 1 added a profile-scored verdict card but left the
 * Decision tab, the goal-seek solver and the PDF reading
 * STRATEGY_THRESHOLDS[investment_strategy]. Bear Valley then rendered NO-GO and
 * GO on one screen, and the Optimization tab solved max price against a 10.00%
 * Core-Plus target for a deal whose investor bar is 7.00%. Every consumer must
 * call this - see [[the defect class]]: fixing one path and missing the others
 * is how this bug has recurred seven times.
 *
 * Returns STRATEGY_THRESHOLDS rows BY REFERENCE in the fallback case, so the
 * pinned identity test keeps holding and saved analyses cannot be re-scored.
 */
export function resolveActiveThresholds(
  profile: InvestorProfileThresholds | null | undefined,
  strategy: InvestmentStrategy
): StrategyThresholds {
  if (!profile) return STRATEGY_THRESHOLDS[strategy];
  // Mirrors the ?? defaults the verdict card already used, so the card's
  // behaviour is unchanged and only the other consumers move onto it.
  return {
    cap_rate: profile.threshold_cap_rate ?? null,
    cash_on_cash: profile.threshold_cash_on_cash ?? 0,
    irr: profile.threshold_irr ?? 0,
    equity_multiple: profile.threshold_equity_multiple ?? 0,
    dscr: profile.threshold_dscr ?? 0,
  };
}

/**
 * What each strategy is CALLED. One map, because pdfExport.ts carried its own copy of this
 * ternary and a second copy is a second chance to disagree.
 */
export const STRATEGY_LABELS: Record<InvestmentStrategy, string> = {
  core: 'Core',
  core_plus: 'Core-Plus',
  value_add: 'Value-Add',
  opportunistic: 'Opportunistic',
};

/**
 * ⭐⭐⭐ THE LABEL FOR THE BAR THIS DEAL IS ACTUALLY HELD TO.
 *
 * The companion to `resolveActiveThresholds`, and it exists for the same reason. That function
 * unified the NUMBERS across every consumer on 2026-07-31; the LABEL was left behind as an
 * inline `activeProfile?.name ?? 'Core-Plus'` at three call sites - the verdict card, the
 * decision scorecard and the PDF payload. With no investor profile set, which is the default on
 * EVERY freshly extracted deal, one screen read "Decision Scorecard - Core-Plus" beside
 * "Scorecard - Opportunistic Strategy" while grading on the opportunistic bar. Found 2026-08-31
 * by opening a fresh deal and reading it.
 *
 * ⛔ It takes the SAME two inputs as `resolveActiveThresholds` and resolves them the SAME way,
 * which is what makes the words and the numbers unable to disagree. Every consumer must call it.
 */
export function resolveThresholdLabel(
  profile: (InvestorProfileThresholds & { name?: string | null }) | null | undefined,
  strategy: InvestmentStrategy
): string {
  // An unnamed profile falls through to the strategy rather than printing an empty bar.
  if (profile?.name) return profile.name;
  return `${STRATEGY_LABELS[strategy]} Strategy`;
}

export const STRUCTURE_SUPPRESSED_METRICS: Record<CapitalStructure, string[]> = {
  equity_first: [],
  ltv_driven: [],
  all_cash: ['dscr', 'debt_yield'],
  assume_existing: [],
  seller_carry: [],
  sba_504: [],
  sba_7a: [],
};

const CORPUS_COST_CONVENTION = {
  closing_costs_percent: 2,
  selling_costs_percent: 2,
  holding_period: 5,
} as const;

export const ARCHETYPE_PRESETS: Record<Archetype, ArchetypePreset> = {
  stnl_nnn: {
    key: 'stnl_nnn',
    label: 'Single-tenant NNN',
    defaultProfile: 'credit_tenant_yield',
    suppressedMetrics: [],
    descriptorTemplate: 'SINGLE-TENANT NNN STNL | {sf} SF | {occupancy} Leased',
    defaults: {
      lease_type: 'nnn',
      nnn_reimbursement_rate: 100,
      vacancy_rate: 0,
      management_fee_percent: 0,
      property_taxes: 0,
      insurance: 0,
      utilities: 0,
      repairs_maintenance: 0,
      reserves_capex: 0,
      other_expenses: 0,
      income_growth_rate: 0,
      expense_growth_rate: 0,
      ...CORPUS_COST_CONVENTION,
    },
  },
  multi_tenant_retail: {
    key: 'multi_tenant_retail',
    label: 'Multi-tenant NNN retail center',
    defaultProfile: 'core_plus',
    suppressedMetrics: [],
    descriptorTemplate: 'MULTI-TENANT NNN | {sf} SF | {occupancy} Leased',
    defaults: {
      lease_type: 'nnn',
      nnn_reimbursement_rate: 100,
      vacancy_rate: 5,
      management_fee_percent: 4,
      income_growth_rate: 3,
      expense_growth_rate: 3,
      ...CORPUS_COST_CONVENTION,
    },
  },
  value_add_leaseup: {
    key: 'value_add_leaseup',
    label: 'Value-add / lease-up',
    defaultProfile: 'value_add',
    suppressedMetrics: [],
    descriptorTemplate: 'VALUE-ADD / LEASE-UP | {sf} SF | {occupancy} Leased',
    defaults: {
      lease_type: 'nnn',
      vacancy_rate: 10,
      management_fee_percent: 4,
      income_growth_rate: 3,
      expense_growth_rate: 3,
      va_vacancy_leaseup: true,
      // ⭐ V4. The box above is a LABEL read by no calculation; THIS prices the lease-up. The preset
      // named after the lease-up used to tick the box and leave the months empty, so a vacant box
      // priced as stabilised on the day of close. 12 is the category default both delivered
      // lease-up analyses carry (Jackson "~9-12 months", Victorville "12-month lease-up"); a
      // typed or extracted value outranks it, because applyPreset never overwrites those.
      lease_up_months: 12,
      ...CORPUS_COST_CONVENTION,
    },
  },
  owner_user: {
    key: 'owner_user',
    label: 'Owner-user',
    defaultProfile: 'owner_user',
    suppressedMetrics: ['cap_rate', 'irr', 'equity_multiple', 'cash_on_cash'],
    descriptorTemplate: 'OWNER-USER | {sf} SF',
    defaults: {
      lease_type: 'gross',
      vacancy_rate: 0,
      management_fee_percent: 0,
      income_growth_rate: 3,
      expense_growth_rate: 3,
      ...CORPUS_COST_CONVENTION,
    },
  },
  industrial_multi: {
    key: 'industrial_multi',
    label: 'Industrial multi-tenant / flex',
    defaultProfile: 'core_plus',
    suppressedMetrics: [],
    descriptorTemplate: 'MULTI-TENANT INDUSTRIAL | {sf} SF | {occupancy} Leased',
    defaults: {
      lease_type: 'nnn',
      nnn_reimbursement_rate: 100,
      vacancy_rate: 5,
      management_fee_percent: 3,
      income_growth_rate: 3,
      expense_growth_rate: 3,
      ...CORPUS_COST_CONVENTION,
    },
  },
  ground_lease: {
    key: 'ground_lease',
    label: 'Ground lease',
    defaultProfile: 'core',
    suppressedMetrics: [],
    descriptorTemplate: 'ABSOLUTE NNN GROUND LEASE | {sf} SF',
    defaults: {
      lease_type: 'nnn',
      nnn_reimbursement_rate: 100,
      vacancy_rate: 0,
      management_fee_percent: 0,
      property_taxes: 0,
      insurance: 0,
      utilities: 0,
      repairs_maintenance: 0,
      reserves_capex: 0,
      income_growth_rate: 0,
      expense_growth_rate: 0,
      ...CORPUS_COST_CONVENTION,
    },
  },
  office: {
    key: 'office',
    label: 'Office',
    defaultProfile: 'core_plus',
    suppressedMetrics: [],
    descriptorTemplate: 'OFFICE | {sf} SF | {occupancy} Leased',
    defaults: {
      lease_type: 'modified_gross',
      vacancy_rate: 10,
      management_fee_percent: 4,
      income_growth_rate: 3,
      expense_growth_rate: 3,
      ...CORPUS_COST_CONVENTION,
    },
  },
  multifamily: {
    key: 'multifamily',
    label: 'Multifamily',
    defaultProfile: 'core_plus',
    suppressedMetrics: [],
    descriptorTemplate: 'MULTIFAMILY | {units} Units | {occupancy} Occupied',
    defaults: {
      lease_type: 'gross',
      vacancy_rate: 5,
      management_fee_percent: 5,
      income_growth_rate: 3,
      expense_growth_rate: 3,
      ...CORPUS_COST_CONVENTION,
    },
  },
  land_development: {
    key: 'land_development',
    label: 'Land / development',
    defaultProfile: 'opportunistic',
    suppressedMetrics: ['cap_rate', 'dscr', 'cash_on_cash'],
    descriptorTemplate: 'LAND / DEVELOPMENT | {lotSize}',
    defaults: {
      vacancy_rate: 0,
      management_fee_percent: 0,
      income_growth_rate: 0,
      expense_growth_rate: 0,
      ...CORPUS_COST_CONVENTION,
    },
  },
};
